import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  getUnexpiredToken,
  useActiveMainCanvas,
  useActiveProject,
  useCanvasId,
} from "@/utils/utils";
import { Input } from "./ui/input";
import {
  ChevronDownIcon,
  Loader2,
  ExternalLink,
  CheckCircle,
  Info,
  TriangleAlert,
  Clipboard,
} from "lucide-react";
import {
  ReactFramework,
  TEMPLATE_PROJECT_PATHS,
} from "@/utils/templateProjects";
import { useRunTerminalCommand } from "@/utils/devServerUtils";
import { useSupabase } from "./contexts/DatabaseSyncWrapper";
import { cn } from "@/lib/utils";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";
import { useDispatch } from "react-redux";
import { track } from "@/utils/events/tracker";
import Console from "./Console";
import { AddMembers } from "./AddMembers";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuGroup,
} from "./ui/dropdown-menu";
import { v4 as uuidv4 } from "uuid";
import {
  ManualAssistantMessage,
  useAiContextStore,
} from "@/store/zustand/aiContextStore";
import { useCanvasStore } from "@/store/zustand/canvasStore";
import axios from "axios";
import { clearAllInternalErrors } from "@/store/iframeSlice";
import {
  useAiActionStore,
  useCurrentConvo,
} from "@/store/zustand/aiActionStore";
import { useIsChatLoading } from "@/store/zustand/aiContextStore";
import { useDevServerStore } from "@/store/zustand/devServerStore";
import useLayoutStore, { LeftTab } from "@/store/zustand/layoutStore";
import { useSession } from "@clerk/clerk-react";
import { toast } from "sonner";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { useProjectModalStore } from "@/store/zustand/projectModalStore";
import { useIssuesStore } from "@/store/zustand/issues-store";
import { Canvas } from "@/utils/types";

interface Props {
  closeModal: () => void;
}

interface VercelFileError {
  name: string;
  size: string;
}

const getDeployCommand = (canvasId: string, framework: ReactFramework) => {
  let basePath = `/${canvasId}`;
  if (import.meta.env.VITE_ENVIRONMENT === "DEV") {
    basePath = `/preview${basePath}`;
  }

  let command, envVariables;

  if (
    framework === ReactFramework.NEXT_JS ||
    framework === ReactFramework.NEXT_JS_APP_ROUTER
  ) {
    command = `npm run build && mv out dist`;
    envVariables = {
      NEXT_PUBLIC_OUTPUT_MODE: "export",
      NEXT_PUBLIC_BASE_PATH: basePath,
      NEXT_PUBLIC_IGNORE_ERRORS: "true",
      TEMPO: "false",
      NEXT_PUBLIC_TEMPO: "false",
    };
  } else if (framework === ReactFramework.VITE) {
    command = `npm run build`;
    envVariables = {
      VITE_BASE_PATH: basePath,
      VITE_IGNORE_ERRORS: "true",
      TEMPO: "false",
      VITE_TEMPO: "false",
    };
  }

  // TODO: Remix
  return {
    command,
    envVariables,
  };
};

const getPreviewUrlForCanvas = (canvasId: string) => {
  if (import.meta.env.VITE_ENVIRONMENT === "DEV") {
    return `http://localhost:3001/preview/${canvasId}`;
  }

  if (import.meta.env.VITE_ENVIRONMENT === "STAGING") {
    return `https://staging-preview.tempo-dev.app/${canvasId}`;
  }

  // Important - this points to the router, which then points to the correct machine
  return `https://view.tempo-dev.app/${canvasId}`;
};

const ShareModal: React.FC<Props> = (props) => {
  const [selectedDeployOption, setSelectedDeployOption] = useState<
    "vercel" | "tempo"
  >("vercel");
  const setTempAssistantMessage = useAiContextStore(
    (state) => state.setTempAssistantMessage
  );
  const setAddManualMessage = useAiContextStore(
    (state) => state.setAddManualMessage
  );
  const clearTerminalErrors = useDevServerStore(
    (state) => state.clearTerminalErrors
  );
  const addTerminalErrors = useDevServerStore(
    (state) => state.addTerminalErrors
  );
  const clearStoryboardIdToErrors = useDevServerStore(
    (state) => state.clearStoryboardIdToErrors
  );
  const terminalErrors = useDevServerStore((state) => state.terminalErrors);
  const { session } = useSession();

  const updateCanvas = useIssuesStore((state) => state.updateCanvas);

  const canvasId = useCanvasId();

  const canvas = useActiveMainCanvas();
  const canvasRef = useRef(canvas);
  useEffect(() => {
    canvasRef.current = canvas;
  }, [canvas]);

  const project = useActiveProject();
  const dispatch = useDispatch();

  const [deploying, setDeploying] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  const runTerminalCommand = useRunTerminalCommand();

  const onDbSnapshot = useSupabaseStore((state) => state.onDbSnapshot);

  const supabaseClient = useSupabaseStore((state) => state.supabaseClient);

  const [latestVercelDeployment, setLatestVercelDeployment] = useState(null);
  const { conversation, messages } = useCurrentConvo();
  const selectedConversationId = conversation?.id;

  const isChatLoading = useIsChatLoading();

  const setNewLayout = useLayoutStore((store) => store.setNewLayout);

  useEffect(() => {
    const fetchDeployments = async () => {
      if (!supabaseClient) return;

      const { data, error } = await supabaseClient
        .from("vercel_deployments")
        .select("*")
        .eq("canvas_id", canvasId)
        .order("created_at", { ascending: false })
        .limit(1);

      if (error) {
        console.error(error);
      } else {
        setLatestVercelDeployment(data[0]);
      }
    };

    fetchDeployments();
  }, [canvasId, supabaseClient]);

  const command = useMemo(() => {
    return `npx tempo connect ${canvasId}`;
  }, [canvasId]);

  const copy = (value, setter: (value: boolean) => void) => {
    navigator.clipboard.writeText(value);
    setter(true);
    setTimeout(() => {
      setter(false);
    }, 1000);
  };

  const deployWithVercel = async () => {
    if (isChatLoading || !supabaseClient) return;

    // open ai chat to show deployment status & errors
    setNewLayout(LeftTab.AIChat);

    // clear errors before rebuilding
    clearStoryboardIdToErrors();
    dispatch(clearAllInternalErrors());
    clearTerminalErrors();

    const msg: ManualAssistantMessage = {
      id: uuidv4().toString(),
      text: "Preparing project for deployment with Vercel...",
      progressItems: [
        { title: "Building project", loading: true },
        { title: "Deploying to Vercel" },
      ],
    };
    setTempAssistantMessage({ ...msg });
    const { success, error } = await runTerminalCommand({
      command: "npm run build",
    });

    if (!success) {
      // @ts-expect-error TS18048
      msg.progressItems.forEach((item) => (item["failed"] = true));
      msg.afterText = "Project failed to deploy to Vercel. Please try again.";
      // @ts-expect-error TS2345
      setTempAssistantMessage(null);
      setAddManualMessage({ ...msg });
      return;
    }

    if (terminalErrors.length > 0) {
      // @ts-expect-error TS18048
      msg.progressItems.forEach((item) => (item["failed"] = true));
      msg.afterText =
        "Project failed to build with errors. Please fix the errors and try again.";
      // @ts-expect-error TS2345
      setTempAssistantMessage(null);
      setAddManualMessage({ ...msg });
      return;
    }

    // Success case
    // @ts-expect-error TS18048
    msg.progressItems[0]["succeeded"] = true;
    // @ts-expect-error TS18048
    msg.progressItems[1]["loading"] = true;
    setTempAssistantMessage({ ...msg });

    if (!project?.id) {
      // deploy failed
      // @ts-expect-error TS18048
      msg.progressItems[1]["failed"] = true;
      msg.afterText = "Project failed to deploy to Vercel";
      setAddManualMessage({ ...msg });
      // @ts-expect-error TS2345
      setTempAssistantMessage(null);
      return;
    }

    const supabaseJWT = await getUnexpiredToken(session);

    const response = await axios.post(
      `${
        import.meta.env.VITE_BACKEND_BASE_URL
      }/canvases/${canvasId}/deploy/vercel`,
      {
        // canvasId: canvasId,
        projectId: project.id,
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${supabaseJWT}`,
        },
        validateStatus: (status) => true,
      }
    );

    // @ts-expect-error TS2345
    setTempAssistantMessage(null);
    if (response.status === 200) {
      const addMessageToDb = async () => {
        const fullTextContent =
          '\n<vercel_deployment_message>\n{"items":' +
          JSON.stringify({
            previewUrl: response.data.previewUrl,
            claimUrl: response.data.claimUrl,
          }) +
          "}\n</vercel_deployment_message>";

        const newMessage: any = {
          id: uuidv4().toString(),
          conversation_id: selectedConversationId,
          canvas_id: canvasId,
          content: fullTextContent,
          role: "assistant",
          message_context: null,
          tool_calls: null,
          tool_call_id: null,
          created_at: new Date().toISOString(),
        };

        onDbSnapshot("aiChatMessages", {
          newItems: [newMessage],
        });

        await supabaseClient.from("ai_chat_messages").insert(newMessage);
      };

      addMessageToDb();
    } else {
      // deploy failed
      // @ts-expect-error TS18048
      msg.progressItems[1]["failed"] = true;
      const vercelStringifiedError = JSON.stringify(
        response.data?.error?.message ||
          response.data?.error ||
          response.data ||
          "Unknown error occurred while deploying to Vercel: " + response.status
      );

      const vercelFileErrors: VercelFileError[] =
        response?.data?.details?.files || [];

      msg.afterText =
        vercelFileErrors.length > 0
          ? `Project failed to deploy to Vercel. ${vercelFileErrors.length} file(s) had issues. Error: ${vercelStringifiedError}.`
          : `Project failed to deploy to Vercel. Error: ${vercelStringifiedError}.`;

      if (vercelFileErrors && vercelFileErrors.length > 0) {
        const terminalErrors = vercelFileErrors.map(
          (fileError: VercelFileError) => ({
            message: vercelStringifiedError,
            filename: `${fileError.name} (${fileError.size})`,
            stack: "",
            count: 1,
          })
        );
        addTerminalErrors(terminalErrors);
      } else {
        addTerminalErrors([
          {
            message: vercelStringifiedError,
            filename: "",
            stack: "",
            count: 1,
          },
        ]);
      }
      setAddManualMessage({ ...msg });
    }

    return;
  };

  let isDeployDisabled;
  if (selectedDeployOption === "vercel") {
    isDeployDisabled = isChatLoading;
  } else {
    isDeployDisabled =
      deploying ||
      (project?.framework !== ReactFramework.NEXT_JS &&
        project?.framework !== ReactFramework.VITE &&
        project?.framework !== ReactFramework.NEXT_JS_APP_ROUTER);
  }

  // Register with project modal store
  useEffect(() => {
    useProjectModalStore.getState().addProjectModal("share-modal");
    return () => {
      useProjectModalStore.getState().removeProjectModal("share-modal");
    };
  }, []);

  return (
    <Dialog
      open={true}
      onOpenChange={() => {
        props.closeModal();
      }}
    >
      <DialogContent className="bg-secondary-0 text-white p-0 max-w-[400px] rounded-lg">
        <div className="divide-y divide-secondary-n-950 flex flex-col py-4 max-w-[400px]">
          <DialogHeader className="px-6 py-2 pt-0">
            <DialogTitle className="text-base text-white text-left font-medium">
              Share
            </DialogTitle>
            <DialogDescription></DialogDescription>
          </DialogHeader>

          <div
            className={cn(
              "px-6 py-4",
              project?.framework === ReactFramework.EXPO && "hidden"
            )}
          >
            <div className="flex flex-col w-full">
              <div className="flex flex-row gap-2 items-center w-full">
                <div className="flex flex-col flex-1 min-w-0">
                  <div className="text-sm font-medium text-white mb-2">
                    Deploy your project
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <div className="flex flex-row bg-secondary-n-950 text-white text-xs h-8 px-2 rounded-md items-center border border-secondary-n-900">
                        <p className="truncate">
                          {selectedDeployOption === "vercel"
                            ? "Vercel"
                            : "Tempo (Beta)"}
                        </p>
                        <ChevronDownIcon className="ml-auto w-3 h-3 text-secondary-800 shrink-0" />
                      </div>
                    </DropdownMenuTrigger>
                    <div className="flex flex-col w-full">
                      <DropdownMenuContent
                        className="text-sm dropdown-content-width-full"
                        sideOffset={5}
                        align="start"
                      >
                        <DropdownMenuGroup className="self-start">
                          <DropdownMenuItem
                            onClick={() => setSelectedDeployOption("vercel")}
                            className="hover:bg-primary-0 cursor-pointer"
                          >
                            <p className="text-xs">Vercel</p>
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => setSelectedDeployOption("tempo")}
                            className="hover:bg-primary-0 cursor-pointer"
                          >
                            <p className="text-xs">Tempo (Beta)</p>
                          </DropdownMenuItem>
                        </DropdownMenuGroup>
                      </DropdownMenuContent>
                    </div>
                  </DropdownMenu>
                </div>
                <Button
                  type="submit"
                  disabled={isDeployDisabled}
                  style={{
                    border: "1px solid #2F3035",
                  }}
                  className={`bg-secondary-n-950 text-white whitespace-nowrap text-xs h-8 px-2 shrink-0 self-end rounded-SM ${
                    isChatLoading && selectedDeployOption === "vercel"
                      ? "opacity-50 cursor-not-allowed"
                      : ""
                  }`}
                  onClick={async () => {
                    if (isChatLoading && selectedDeployOption === "vercel")
                      return;
                    if (!project || !canvasId) return;

                    if (selectedDeployOption === "vercel") {
                      props.closeModal();
                      track("DEPLOY_APP", {
                        framework: project?.framework,
                        deployOption: "vercel",
                      });
                      deployWithVercel();
                      return;
                    }

                    const { command: deployCommand, envVariables } =
                      getDeployCommand(
                        canvasId,
                        project?.framework as ReactFramework
                      );

                    setDeploying(true);
                    setShowLogs(true);
                    track("DEPLOY_APP", {
                      framework: project?.framework,
                      deployOption: "tempo",
                    });

                    const { success, error } = await runTerminalCommand({
                      command: deployCommand,
                      extraEnvVariables: envVariables,
                    });

                    setDeploying(false);
                    if (success) {
                      const canvasPayload: Partial<Canvas> = {
                        last_deployed_ts: new Date().toISOString(),
                      };
                      updateCanvas(
                        supabaseClient,
                        canvasId,
                        canvasPayload as Canvas
                      );
                    }
                  }}
                >
                  <div
                    className={cn("animate-spin mr-2", !deploying && "hidden")}
                  >
                    <Loader2 className="h-3 w-3" />
                  </div>
                  <div className="flex flex-row gap-2 items-center">
                    <img
                      src={
                        selectedDeployOption === "vercel"
                          ? "/images/vercel-logo.svg"
                          : "/icons/tempo-logo-white.svg"
                      }
                      className="w-3.5 h-3.5 text-white"
                    />
                    <div className="w-[1px] h-8 bg-[#2F3035]" />
                    <span>
                      {(canvas?.last_deployed_ts &&
                        selectedDeployOption === "tempo") ||
                      (latestVercelDeployment &&
                        selectedDeployOption === "vercel")
                        ? "Re-deploy"
                        : "Deploy"}
                    </span>
                  </div>
                </Button>
              </div>

              {latestVercelDeployment && selectedDeployOption === "vercel" && (
                <div className="flex flex-col gap-4">
                  <div className="flex flex-row justify-between items-center space-x-2 mt-4 bg-secondary-n-900 p-1.5 rounded-md">
                    <div className="bg-secondary-n-950 text-white border rounded-md py-1 px-2 overflow-hidden flex flex-row items-center gap-2">
                      <p
                        className="truncate text-xs text-gray-100 font-['Inconsolata'] cursor-pointer"
                        onClick={() =>
                          window.open(
                            // @ts-expect-error TS2339
                            `https://${latestVercelDeployment.deployment_url}`,
                            "_blank"
                          )
                        }
                      >
                        {/*
                         // @ts-expect-error TS2339 */}
                        https://{latestVercelDeployment.deployment_url}
                      </p>
                      <CheckCircle className="w-3 h-3 text-confirm-500 shrink-0" />
                    </div>
                    <div className="flex pr-1">
                      <ExternalLink
                        className="cursor-pointer w-4 h-4 text-secondary-n-600 shrink-0"
                        onClick={() =>
                          window.open(
                            // @ts-expect-error TS2339
                            `https://${latestVercelDeployment.deployment_url}`,
                            "_blank"
                          )
                        }
                      />
                    </div>
                  </div>
                  <div className="rounded-md border border-secondary-n-900 p-2">
                    <div className="flex flex-row items-center gap-2">
                      {/*
                       // @ts-expect-error TS2339 */}
                      {latestVercelDeployment.claimed && (
                        <Info className="w-3.5 h-3.5 text-secondary-800 shrink-0 mb-auto" />
                      )}
                      <p className="text-xs text-gray-100">
                        {/*
                         // @ts-expect-error TS2339 */}
                        {latestVercelDeployment.claimed
                          ? "You've claimed your project! Manage or deploy to a custom domain in Vercel."
                          : `Your deployment is live! Claim it to manage in Vercel and deploy to a custom domain—link expires in 48 hours.\n`}
                      </p>
                    </div>
                    {/*
                     // @ts-expect-error TS2339 */}
                    {!latestVercelDeployment.claimed && (
                      <div className="flex flex-col gap-2 mt-2">
                        <span className="flex flex-row items-center gap-2">
                          <TriangleAlert className="w-4 h-4 text-[#E17100] shrink-0" />
                          <p className="text-xs text-secondary-800">
                            This link expires in 48 hours.
                          </p>
                        </span>
                        <Button
                          variant="default"
                          className="text-xs bg-primary-0 hover:bg-primary-100 whitespace-nowrap rounded-md h-8"
                          onClick={() =>
                            window.open(
                              // @ts-expect-error TS2339
                              `https://vercel.com/claim-deployment?code=${latestVercelDeployment.claim_code}`,
                              "_blank"
                            )
                          }
                        >
                          Claim Now
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {canvas?.last_deployed_ts && selectedDeployOption === "tempo" && (
                <div className="flex flex-col gap-4">
                  <div className="flex flex-row justify-between items-center space-x-2 mt-4 bg-secondary-n-900 p-1.5 rounded-md">
                    <div className="bg-secondary-n-950 text-white border rounded-md py-1 px-2 overflow-hidden flex flex-row items-center gap-2">
                      <p
                        className="truncate text-xs text-gray-100 font-['Inconsolata'] cursor-pointer"
                        onClick={() =>
                          window.open(
                            getPreviewUrlForCanvas(canvasId),
                            "_blank"
                          )
                        }
                      >
                        {getPreviewUrlForCanvas(canvasId)}
                      </p>
                      <CheckCircle className="w-3 h-3 text-confirm-500 shrink-0" />
                    </div>
                    <div className="flex pr-1">
                      <ExternalLink
                        className="cursor-pointer w-3.5 h-3.5 text-secondary-n-600 shrink-0"
                        onClick={() =>
                          window.open(
                            getPreviewUrlForCanvas(canvasId),
                            "_blank"
                          )
                        }
                      />
                    </div>
                  </div>
                  <div className="rounded-md border border-secondary-n-900 p-2">
                    <div className="flex flex-row items-center gap-2">
                      <Info className="w-4 h-4 text-secondary-800 shrink-0 mb-auto" />
                      <p className="text-xs text-gray-100">
                        If your deploy has errors, check the logs. Copy and
                        paste them into the AI chat for help.{" "}
                        <span
                          className="text-primary-0 cursor-pointer"
                          onClick={() => setShowLogs(!showLogs)}
                        >
                          Show logs.
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* {canvas?.last_deployed_ts && selectedDeployOption === "tempo" && (
                <div className="flex flex-row justify-between items-center space-x-2 mt-4">
                  <p className="text-xs text-white font-medium whitespace-nowrap">
                    Tempo URL:{" "}
                  </p>
                 <div
                    onBeforeInput={() => {}}
                    className="bg-secondary-200 text-white border-secondary-500 text-xs border rounded-md p-2 overflow-x-scroll hide-scrollbar whitespace-nowrap flex-1"
                  >
                    {getPreviewUrlForCanvas(canvasId)}
                  </div>
                  <Button
                    variant="default"
                    className="text-xs bg-secondary-200 hover:bg-secondary-400 whitespace-nowrap"
                    onClick={() =>
                      copy(getPreviewUrlForCanvas(canvasId), setCopiedDeploy)
                    }
                    disabled={copiedDeploy}
                  >
                    {copiedDeploy ? "Copied!" : "Copy Link"}
                  </Button>
                </div>
              )} */}
            </div>
          </div>

          {(canvas?.last_deployed_ts || deploying) &&
            selectedDeployOption === "tempo" &&
            showLogs && (
              <div className="flex flex-row justify-between items-center space-x-2 px-6 py-2">
                <Button
                  variant="default"
                  className="text-xs bg-secondary-200 hover:bg-secondary-400 whitespace-nowrap h-8"
                  onClick={() => setShowLogs(!showLogs)}
                >
                  Hide logs
                </Button>
              </div>
            )}

          <div
            className={cn(
              !deploying && !showLogs && "hidden",
              "min-h-0 h-[50vh] p-4 pb-0"
            )}
          >
            <Console readOnly connect={deploying || showLogs} />
          </div>

          <div
            className={cn(
              "divide-y divide-secondary-n-950",
              (showLogs || deploying) && "hidden"
            )}
          >
            <AddMembers type="canvas" projectId={project?.id || ""} />
            {/* <div className="px-6 py-4">
              <div className="flex flex-col">
                <div className="text-sm font-bold text-white mb-2">
                  Share my canvas
                </div>
                <div className="text-xs text-secondary-900">
                  Share this link with other members who have access to the
                  project. Your canvas is unique to you—sharing it with others
                  will allow them to see your changes in real-time.
                </div>
                <div className="flex flex-row justify-between items-center space-x-2 mt-4">
                  <Input
                    onBeforeInput={() => {}}
                    className="bg-secondary-200 text-white border-secondary-500 text-xs py-0"
                    value={shareLink}
                  />
                  <Button
                    variant="default"
                    className="text-xs bg-secondary-200 hover:bg-secondary-400 whitespace-nowrap"
                    onClick={() => copy(shareLink, setCopied)}
                    disabled={copied}
                  >
                    {copied ? "Copied!" : "Copy Link"}
                  </Button>
                </div>
              </div>
            </div> */}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default React.memo(ShareModal);
